// =============================================================================
// SIREPPAR - Documentación del expediente, POR SECCIÓN
// =============================================================================
// Se llamaba PanelTiposDocumentoAlianza. Dejó de ser de alianzas: reemplazó al
// tablero de documentación en TODOS los trámites (reempadronamiento y reconocimiento
// incluidos), porque el problema que resuelve es el mismo en los tres.
// =============================================================================
// RESUMEN, de solo lectura. Una fila por TIPO con el estado que declaró la
// Secretaría, su nota y quién la firmó.
//
// El CHECK para aprobar NO está acá: vive en /documentos, que es la pantalla donde
// se revisa la documentación y donde están los archivos. Tener dos lugares para el
// mismo acto es pedir que se contradigan.
//
// Por qué la sección tiene su propio estado y no se deriva de los documentos: en
// una alianza hay tipos con N archivos (una constancia de voluntad por integrante,
// un acta por apoderado, una por órgano) y con cada observación ese N crece. Contar
// documentos no puede saber cuántos hacen falta: con tres integrantes y una sola
// constancia aprobada, el tipo figuraba verificado. Quien sí sabe es la supervisora,
// y acá tiene dónde declararlo y dejar una nota.
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { seccionesDocumentoService, api } from '../../../services/api';
import CuadroNota from '../../../components/common/CuadroNota';
import { GRUPOS_TIPOS_DOCUMENTO, GRUPOS_LABELS } from '../../../utils/tiposDocumento';
import { archivosNuevosSinRevisar, avisoArchivoNuevo } from '../../../utils/archivoNuevo';
import { resumirEnUnaLinea } from '../../../components/common/TextoColapsable';
import { RESUMEN_MOTIVO_LINEA } from '../../../utils/limites';
import { pesaEnDimension } from '../../../utils/etapaVerificacion';

type EstadoRevision = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

export interface DocumentoDeTipo {
  id: string;
  titulo: string;
  nombreOriginal: string;
  creadoEn: string;
  estadoRevisionAdmisibilidad: EstadoRevision;
  observacionAdmisibilidad: string | null;
  admisibilidadRevisadoEn?: string | null;
  estadoRevisionAnalisis: EstadoRevision;
  observacionAnalisis: string | null;
  /** Cuándo se revisó este archivo. Sirve de corte para el aviso de "archivo nuevo". */
  analisisRevisadoEn?: string | null;
  dniPersona: string | null;
}

export interface EstadoDimension {
  estado: EstadoRevision;
  revisadoPorNombre: string | null;
  revisadoEn: string | null;
}

export interface TipoDocumentoSeccion {
  id: string;
  tipo: string;
  label: string;
  obligatorio: boolean;
  /** Nota interna de la Secretaría sobre la sección. Una sola, no por dimensión. */
  nota: string | null;
  // Las dos dimensiones, igual que en `Documento`. Cuál se muestra lo decide la
  // etapa que el expediente ya alcanzó, no el panel: en alianzas resuelve el
  // análisis, pero un reempadronamiento que todavía está en admisibilidad se revisa
  // en la OTRA dimensión, y mostrarle la de análisis dejaba todo en "Sin revisar"
  // aunque la analista acabara de aprobarlo.
  admisibilidad: EstadoDimension;
  analisis: EstadoDimension;
  documentos: DocumentoDeTipo[];
}

interface Props {
  expedienteId: string;
  partidoId?: string;
  secciones: TipoDocumentoSeccion[];
  /** La NOTA sí se edita acá: es un comentario, no una aprobación. Las
   *  aprobaciones siguen viviendo sólo en /documentos. */
  puedeEditarNota?: boolean;
  /** Dimensión que se muestra. La decide la etapa alcanzada por el expediente
   *  (`dimensionPorDefecto` de utils/etapaVerificacion), no este componente. */
  dimension?: 'ADMISIBILIDAD' | 'ANALISIS';
  /** Encabezado del bloque. En alianzas cita el Art. 27; en los demás trámites la
   *  documentación sale de otros artículos, así que el título lo pone quien usa el
   *  panel en vez de estar clavado acá. */
  tituloSeccion?: string;
  /** Muestra el cuadro de nota. La nota es el comentario de trabajo de la
   *  Secretaría sobre la sección, no un acto formal dirigido a la agrupación: en la
   *  vista de la agrupación la columna no se dibuja. El backend además no le manda
   *  el texto (ver tiposDocumentoDeExpediente), así que sin esto quedaba un guión
   *  suelto ocupando la columna. */
  mostrarNota?: boolean;
}

const ESTILO_SECCION: Record<EstadoRevision, { caja: string; badge: string; texto: string }> = {
  APROBADO: { caja: 'border-green-300 bg-green-50', badge: 'bg-green-100 text-green-800', texto: 'Aprobada' },
  RECHAZADO: { caja: 'border-red-300 bg-red-50', badge: 'bg-red-100 text-red-800', texto: 'Rechazada' },
  PENDIENTE: { caja: 'border-gray-200 bg-white', badge: 'bg-gray-100 text-gray-700', texto: 'Sin revisar' },
};

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/**
 * Ordena los archivos de una sección del más reciente al más viejo, que es el orden
 * en que importan: lo último que se subió es lo que hay que mirar y las versiones
 * anteriores quedan debajo como antecedente.
 *
 * SIN agrupar ni separar por persona: el título del archivo ya dice a quién
 * corresponde, así que el separador con el nombre repetía el mismo dato y partía la
 * lista en pedazos.
 */
function ordenarPorFecha(docs: DocumentoDeTipo[]): DocumentoDeTipo[] {
  const ts = (d: DocumentoDeTipo) => new Date(d.creadoEn).getTime();
  return [...docs].sort((a, b) => ts(b) - ts(a));
}

export default function PanelSeccionesDocumentacion({ expedienteId, partidoId, secciones, puedeEditarNota, mostrarNota = true, tituloSeccion, dimension = 'ANALISIS' }: Props) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const notaMutation = useMutation({
    mutationFn: ({ tipo, nota }: { tipo: string; nota: string }) =>
      seccionesDocumentoService.nota(expedienteId, tipo, nota),
    onSuccess: () => {
      toast.success('Nota guardada');
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteId] });
      queryClient.invalidateQueries({ queryKey: ['secciones-tipo-docs', expedienteId] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo guardar la nota'),
  });

  // Donde SÍ se aprueba: /documentos, con los archivos a la vista.
  const hrefDocumentos = `/documentos?expedienteId=${expedienteId}${partidoId ? `&partidoId=${partidoId}` : ''}`;

  const abrirDoc = async (docId: string) => {
    try {
      const res = await api.get(`/documentos/${docId}/url`);
      const url = res.data.data?.url || res.data.url;
      if (url) window.open(url, '_blank');
    } catch { toast.error('No se pudo abrir el documento'); }
  };

  // Los DNI vienen como CUATRO filas (frente y dorso × autoridades y apoderados),
  // porque la tabla se indexa por tipo. Acá se colapsan a DOS secciones, que es la
  // unidad con la que se trabaja: "los DNI de las autoridades están" es una sola
  // decisión, no dos. El estado combinado es el más conservador.
  const colapsarDni = (lista: TipoDocumentoSeccion[]): TipoDocumentoSeccion[] => {
    const salida: TipoDocumentoSeccion[] = [];
    const yaHecho = new Set<string>();
    for (const s of lista) {
      const grupo = Object.entries(GRUPOS_TIPOS_DOCUMENTO)
        .find(([, tipos]) => tipos.includes(s.tipo))?.[0];
      if (!grupo) { salida.push(s); continue; }
      if (yaHecho.has(grupo)) continue;
      yaHecho.add(grupo);
      const filas = lista.filter((x) => GRUPOS_TIPOS_DOCUMENTO[grupo].includes(x.tipo));
      const combinar = (dim: 'admisibilidad' | 'analisis'): EstadoDimension => {
        const estados = filas.map((f) => f[dim].estado);
        const estado: EstadoRevision = estados.some((e) => e === 'RECHAZADO')
          ? 'RECHAZADO'
          : estados.every((e) => e === 'APROBADO') ? 'APROBADO' : 'PENDIENTE';
        const firmada = filas.find((f) => f[dim].revisadoPorNombre);
        return {
          estado,
          revisadoPorNombre: firmada?.[dim].revisadoPorNombre ?? null,
          revisadoEn: firmada?.[dim].revisadoEn ?? null,
        };
      };
      salida.push({
        id: grupo,
        tipo: grupo,
        label: GRUPOS_LABELS[grupo] ?? grupo,
        obligatorio: filas.some((f) => f.obligatorio),
        nota: filas.find((f) => f.nota)?.nota ?? null,
        admisibilidad: combinar('admisibilidad'),
        analisis: combinar('analisis'),
        // Los archivos de los DOS tipos juntos, en una sola lista por fecha.
        // Concatenar las filas dejaba todos los frentes y después todos los dorsos.
        documentos: ordenarPorFecha(filas.flatMap((f) => f.documentos)),
      });
    }
    return salida;
  };

  // Proyección a la dimensión activa. Es el mismo patrón que usa /documentos: se
  // elige una vez y todo lo de abajo —estado de la sección, estado de cada archivo,
  // motivo, fecha de revisión— habla de esa misma.
  const enAnalisis = dimension === 'ANALISIS';
  const dimDe = (s: TipoDocumentoSeccion) => (enAnalisis ? s.analisis : s.admisibilidad);
  const estadoDoc = (d: DocumentoDeTipo) =>
    enAnalisis ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad;
  const motivoDoc = (d: DocumentoDeTipo) =>
    enAnalisis ? d.observacionAnalisis : d.observacionAdmisibilidad;
  const revisadoEnDoc = (d: DocumentoDeTipo) =>
    enAnalisis ? d.analisisRevisadoEn : d.admisibilidadRevisadoEn;

  // Los archivos que no pesan en la dimensión que se muestra se sacan ACÁ, antes de
  // todo lo demás: así el contador de la sección, el aviso de "archivo nuevo" y la
  // lista expandida hablan del mismo conjunto.
  //
  // Es la MISMA regla que usa /documentos y que usa el backend para contar los
  // pendientes (`pesaEnDimension`, en utils/etapaVerificacion). Antes este panel no
  // la tenía: listaba como "archivo sin revisar" un rechazado en admisibilidad que
  // /documentos escondía, y la supervisora no podía ni encontrarlo ni cerrar la
  // sección.
  const visibles = secciones.map((s) => ({
    ...s,
    documentos: s.documentos.filter((d) => pesaEnDimension(d, dimension)),
  }));

  const agrupadas = colapsarDni(visibles);
  const delArt27 = agrupadas.filter((s) => s.obligatorio);
  const complementarias = agrupadas.filter((s) => !s.obligatorio);
  const aprobadasArt27 = delArt27.filter((s) => dimDe(s).estado === 'APROBADO').length;

  const renderSeccion = (s: TipoDocumentoSeccion) => {
    const dim = dimDe(s);
    const estilo = ESTILO_SECCION[dim.estado];
    // Archivos que entraron DESPUÉS de que se revisó la sección y nadie miró: es lo
    // que se le pasa por alto a la supervisora cuando el partido responde una
    // observación sobre una sección ya aprobada.
    const nuevos = archivosNuevosSinRevisar(
      dim.revisadoEn,
      s.documentos.map((d) => ({
        creadoEn: d.creadoEn, estado: estadoDoc(d), revisadoEn: revisadoEnDoc(d),
      })),
    );
    const aviso = avisoArchivoNuevo(nuevos);
    const expandida = abierta === s.tipo;
    return (
      <div key={s.id} className={`border rounded-lg ${estilo.caja}`}>
        <div className="p-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${estilo.badge}`}>{estilo.texto}</span>
              <p className="font-medium text-gray-900 break-words">{s.label}</p>
              {!s.obligatorio && (
                <span className="px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-600">
                  No exigido
                </span>
              )}
            </div>
            <button
              onClick={() => setAbierta(expandida ? null : s.tipo)}
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              {expandida ? '▼' : '▶'} {s.documentos.length}{' '}
              {s.documentos.length === 1 ? 'archivo' : 'archivos'}
            </button>
            {dim.revisadoPorNombre && dim.estado !== 'PENDIENTE' && (
              <p className="text-xs text-gray-500 mt-0.5">
                {dim.estado === 'APROBADO' ? 'Aprobada' : 'Rechazada'} por {dim.revisadoPorNombre} el {fmt(dim.revisadoEn)}
              </p>
            )}
            {aviso && (
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800">
                <span aria-hidden>●</span> {aviso}
              </p>
            )}
          </div>

          {/* Sin botones de aprobación: el check vive en /documentos. */}
          {/* NOTA: el MISMO cuadro del tablero de documentación de reempadronamiento
              (components/common/CuadroNota) — textarea amarillo con el botón adentro.
              La nota es de la sección y se escribe desde acá o desde /documentos; lo
              que NO se hace acá es aprobar. */}
          {mostrarNota && (
            <div className="w-64 flex-shrink-0">
              <CuadroNota
                valor={s.nota ?? ''}
                puedeEditar={!!puedeEditarNota}
                placeholder="Nota…"
                onGuardar={(nota) => notaMutation.mutateAsync({ tipo: s.tipo, nota })}
              />
            </div>
          )}

          <Link
            // Los grupos de DNI no son un tipo real, así que no sirven para abrir una
            // fila: el link va a la pantalla y la sección se ve de todos modos.
            to={s.tipo.startsWith('GRUPO_') ? hrefDocumentos : `${hrefDocumentos}&abrirTipo=${s.tipo}`}
            className="text-xs text-blue-600 hover:underline flex-shrink-0 whitespace-nowrap"
          >
            Revisar en Documentos →
          </Link>
        </div>

        {expandida && (
          <div className="border-t px-3 py-3 space-y-3 bg-white rounded-b-lg">
            {/* Los archivos, como DETALLE: su estado no decide el de la sección. */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Archivos de la sección
              </p>
              {s.documentos.length === 0 ? (
                <p className="text-sm text-gray-500">Sin archivos cargados.</p>
              ) : (
                <ul className="space-y-1">
                  {s.documentos.map((d) => {
                    return (
                      <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                        <button onClick={() => abrirDoc(d.id)} className="text-blue-600 hover:underline break-all">
                          {d.titulo || d.nombreOriginal}
                        </button>
                        <span className="text-xs text-gray-400">{fmt(d.creadoEn)}</span>
                        {estadoDoc(d) === 'APROBADO' && (
                          <span className="text-[11px] text-green-700 font-medium">✓ archivo aprobado</span>
                        )}
                        {estadoDoc(d) === 'RECHAZADO' && (
                          <span className="text-[11px] text-red-700 font-medium">✕ archivo rechazado</span>
                        )}
                        {/* Motivo del archivo, al costado. Si no tiene, queda en
                            blanco: es lo que hay que leer para saber qué corregir
                            sin abrir /documentos. */}
                        {motivoDoc(d) && (
                          <span className="text-[11px] text-gray-600 italic">
                            Motivo: {resumirEnUnaLinea(motivoDoc(d)!, RESUMEN_MOTIVO_LINEA)}
                          </span>
                        )}
                        {estadoDoc(d) === 'PENDIENTE' && (
                          <span className="text-[11px] text-gray-500">archivo sin revisar</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

            </div>

          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-8">
      <div className="flex items-baseline justify-between border-b pb-2 mb-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          {tituloSeccion ?? 'Documentación — análisis'}
        </h4>
        <div className="flex items-center gap-3">
        
        </div>
      </div>
      <div className="space-y-2">{delArt27.map(renderSeccion)}</div>
      
      {complementarias.length > 0 && (
        <>
          <div className="space-y-2">{complementarias.map(renderSeccion)}</div>
        </>
      )}
    </div>
  );
}
